import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import HCLogo from '../components/HCLogo';
import AppSwitcher from '../components/AppSwitcher';
import LanguageSelector from '../components/LanguageSelector';
import {
  LogOut, LayoutDashboard, Building2, Package, Warehouse, BoxesIcon,
  GitBranch, Hammer, FileText, Briefcase, ClipboardCheck
} from 'lucide-react';
import ErpDashboard from '../components/erp/ErpDashboard';
import PartnersManager from '../components/erp/PartnersManager';
import ProductsManager from '../components/erp/ProductsManager';
import WarehousesManager from '../components/erp/WarehousesManager';
import StockManager from '../components/erp/StockManager';
import BomManager from '../components/erp/BomManager';
import ProductionOrdersManager from '../components/erp/ProductionOrdersManager';
import DocumentsManager from '../components/erp/DocumentsManager';
import InventoryManager from '../components/erp/InventoryManager';

const ErpPage = () => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [tab, setTab] = useState('dashboard');

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="h-14 border-b border-slate-200 bg-white px-4 lg:px-8 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-2.5">
          <HCLogo size="md" />
          <div>
            <div className="text-sm font-semibold text-slate-900 leading-tight">HC ERP</div>
            <div className="text-[11px] text-slate-500 leading-tight">{t('erp.dashboard.subtitle')}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <LanguageSelector size="sm" />
          <div className="hidden sm:flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-indigo-700 text-white flex items-center justify-center text-xs font-semibold">
              {user?.initials}
            </div>
            <div className="text-xs">
              <div className="font-medium text-slate-900">{user?.name}</div>
              <div className="text-slate-500 flex items-center gap-1"><Briefcase className="w-3 h-3" />{t('login.role.erp')}</div>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={logout} className="gap-1.5" data-testid="erp-logout-btn">
            <LogOut className="w-4 h-4" /> {t('common.logout')}
          </Button>
        </div>
      </header>

      <AppSwitcher />

      <main className="max-w-[1600px] mx-auto px-4 lg:px-8 py-6">
        <div className="mb-5">
          <h1 className="text-2xl font-bold text-slate-900">{t('erp.dashboard.title')}</h1>
          <p className="text-sm text-slate-500 mt-1">{t('erp.dashboard.subtitle')}</p>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white border border-slate-200 flex-wrap h-auto mb-4">
            <TabsTrigger value="dashboard" className="gap-2" data-testid="erp-tab-dashboard"><LayoutDashboard className="w-4 h-4" />{t('erp.nav.dashboard')}</TabsTrigger>
            <TabsTrigger value="partners" className="gap-2" data-testid="erp-tab-partners"><Building2 className="w-4 h-4" />{t('erp.nav.partners')}</TabsTrigger>
            <TabsTrigger value="products" className="gap-2" data-testid="erp-tab-products"><Package className="w-4 h-4" />{t('erp.nav.products')}</TabsTrigger>
            <TabsTrigger value="warehouses" className="gap-2" data-testid="erp-tab-warehouses"><Warehouse className="w-4 h-4" />{t('erp.nav.warehouses')}</TabsTrigger>
            <TabsTrigger value="stock" className="gap-2" data-testid="erp-tab-stock"><BoxesIcon className="w-4 h-4" />{t('erp.nav.stock')}</TabsTrigger>
            <TabsTrigger value="inventory" className="gap-2" data-testid="erp-tab-inventory"><ClipboardCheck className="w-4 h-4" />{t('erp.nav.inventory')}</TabsTrigger>
            <TabsTrigger value="bom" className="gap-2" data-testid="erp-tab-bom"><GitBranch className="w-4 h-4" />{t('erp.nav.boms')}</TabsTrigger>
            <TabsTrigger value="production" className="gap-2" data-testid="erp-tab-production"><Hammer className="w-4 h-4" />{t('erp.nav.orders')}</TabsTrigger>
            <TabsTrigger value="documents" className="gap-2" data-testid="erp-tab-documents"><FileText className="w-4 h-4" />{t('erp.nav.documents')}</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="mt-0"><ErpDashboard onJump={setTab} /></TabsContent>
          <TabsContent value="partners" className="mt-0"><PartnersManager /></TabsContent>
          <TabsContent value="products" className="mt-0"><ProductsManager /></TabsContent>
          <TabsContent value="warehouses" className="mt-0"><WarehousesManager /></TabsContent>
          <TabsContent value="stock" className="mt-0"><StockManager /></TabsContent>
          <TabsContent value="inventory" className="mt-0"><InventoryManager /></TabsContent>
          <TabsContent value="bom" className="mt-0"><BomManager /></TabsContent>
          <TabsContent value="production" className="mt-0"><ProductionOrdersManager /></TabsContent>
          <TabsContent value="documents" className="mt-0"><DocumentsManager /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default ErpPage;
